import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Search, X, ArrowRight, Archive, ChevronDown, TrendingUp, TrendingDown } from "lucide-react";
import api from "@/lib/api";

export function PlayerSearch({ className = "" }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [expanded, setExpanded] = useState({});
  const timer = useRef(null);

  const toggle = (name) => setExpanded((prev) => ({ ...prev, [name]: !prev[name] }));

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    const query = q.trim();
    if (query.length < 2) {
      setResults([]);
      setSearched(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    timer.current = setTimeout(() => {
      api
        .get(`/player-search?q=${encodeURIComponent(query)}`)
        .then((r) => {
          setResults(r.data.results || []);
          setSearched(true);
          setExpanded({});
        })
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 350);
    return () => timer.current && clearTimeout(timer.current);
  }, [q]);

  const groups = {};
  results.forEach((r) => {
    (groups[r.player] = groups[r.player] || []).push(r);
  });
  const players = Object.keys(groups);

  return (
    <div className={`bg-white rounded-lg border border-[#E5E7EB] shadow-sm p-5 mb-10 ${className}`} data-testid="player-search">
      <label className="block text-sm font-semibold text-[#111111] mb-2">Find a player/team across all divisions</label>
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-3 h-4 w-4 text-[#999999]" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name…"
          data-testid="player-search-input"
          className="w-full rounded-md border border-[#E5E7EB] pl-9 pr-9 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E63C4]"
        />
        {q && (
          <button onClick={() => setQ("")} data-testid="player-search-clear" aria-label="Clear" className="absolute right-3 top-2.5 text-[#999999] hover:text-[#111111]">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      <p className="text-xs text-[#777777] mt-2" data-testid="player-search-hint">
        Enter a name in either order — e.g. <span className="font-medium text-[#555555]">Smith, John</span> or <span className="font-medium text-[#555555]">John Smith</span>.
      </p>
      <p className="text-xs text-[#999999] mt-1" data-testid="player-rating-note">
        Fargo rating only showing for active players.
      </p>

      {loading && <p className="text-sm text-[#555555] mt-3">Searching…</p>}
      {!loading && searched && players.length === 0 && (
        <p className="text-sm text-[#555555] mt-3" data-testid="player-search-empty">No players found for “{q.trim()}”.</p>
      )}

      {!loading && players.length > 0 && (
        <div className="mt-5 space-y-5" data-testid="player-search-results">
          <p className="text-sm font-semibold text-[#111111]" data-testid="player-search-count">
            {players.length} player{players.length > 1 ? "s" : ""} found
          </p>
          {players.map((name) => (
            <div key={name} className="border border-[#E5E7EB] rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => toggle(name)}
                data-testid={`player-toggle-${name}`}
                aria-expanded={!!expanded[name]}
                className="w-full bg-[#0B0B0C] text-white px-4 py-2.5 font-heading font-bold flex items-center justify-between gap-3 text-left hover:bg-[#1a1a1c] transition-colors"
              >
                <span className="flex items-center gap-2 flex-wrap">
                  {name}
                  {groups[name][0]?.rating && (
                    <span className="text-[11px] font-bold uppercase tracking-wide bg-[#D32F2F] text-white px-2 py-0.5 rounded-full inline-flex items-center gap-1" data-testid={`player-rating-${name}`}>
                      {groups[name][0].rating}
                      {groups[name][0].rating_change === "up" && (
                        <TrendingUp className="h-3.5 w-3.5 text-green-300" data-testid={`player-rating-up-${name}`} />
                      )}
                      {groups[name][0].rating_change === "down" && (
                        <TrendingDown className="h-3.5 w-3.5 text-red-200" data-testid={`player-rating-down-${name}`} />
                      )}
                    </span>
                  )}
                </span>
                <span className="flex items-center gap-2 shrink-0">
                  <span className="text-[11px] font-normal text-gray-400">{groups[name].length} division{groups[name].length > 1 ? "s" : ""}</span>
                  <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${expanded[name] ? "rotate-180" : ""}`} />
                </span>
              </button>
              {expanded[name] && (
              <div className="divide-y divide-[#E5E7EB]" data-testid={`player-divisions-${name}`}>
                {groups[name].map((r, i) => (
                  <Link
                    key={`${r.slug}-${i}`}
                    to={`/divisions/${r.slug}`}
                    data-testid={`player-result-${r.slug}-${i}`}
                    className="block px-4 py-3 hover:bg-blue-50 transition-colors"
                  >
                    <div className="flex items-center justify-between mb-2 gap-3">
                      <span className="font-semibold text-[#111111] flex items-center gap-2 flex-wrap">
                        {r.archived && <Archive className="h-3.5 w-3.5 text-gray-500 shrink-0" />}
                        {r.division_name}
                        {r.season && (
                          <span className="text-[11px] font-semibold uppercase tracking-wide bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{r.season}</span>
                        )}
                        <span className="text-[11px] font-normal text-[#999999]">· {r.table_title}</span>
                      </span>
                      <span className="text-[#1E63C4] text-sm inline-flex items-center gap-1 shrink-0">View <ArrowRight className="h-3.5 w-3.5" /></span>
                    </div>
                    <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm">
                      {r.columns.map((c, ci) => {
                        const val = r.row[ci];
                        if (val == null || String(val).trim() === "" || String(val) === r.player) return null;
                        return (
                          <span key={ci} className="text-[#555555]">
                            <span className="text-[#999999]">{c}:</span> <span className="font-medium text-[#333333]">{val}</span>
                          </span>
                        );
                      })}
                    </div>
                  </Link>
                ))}
              </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
